import { useMemo, useState, useCallback } from 'react';
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, addDays, isSameMonth, isToday, format,
  addMonths, subMonths,
} from 'date-fns';
import { es } from 'date-fns/locale';
import {
  DndContext,
  DragOverlay,
  useDroppable,
  useDraggable,
  pointerWithin,
  type DragStartEvent,
  type DragOverEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { ActividadListItem } from '@/types';

// ─── Constants ────────────────────────────────────────────────────────────────

const WEEKDAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const MAX_VISIBLE = 3;

const PRIORITY_CHIP_COLORS: Record<string, string> = {
  AAA: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  AA: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  A: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  B: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  C: 'bg-gray-50 text-gray-500 dark:bg-gray-800/50 dark:text-gray-400',
};

const PRIORITY_LABELS: [string, string][] = [
  ['AAA', 'Urgente'],
  ['AA', 'Alta'],
  ['A', 'Normal'],
  ['B', 'Baja'],
  ['C', 'Mínima'],
];

const GRID_SIZE = 42; // 6 rows x 7 cols

// ─── Calendar grid helpers ──────────────────────────────────────────────────

interface CalendarDay {
  date: Date;
  dateKey: string;
  isCurrentMonth: boolean;
  isToday: boolean;
}

function getMonthGrid(referenceDate: Date): CalendarDay[] {
  const gridStart = startOfWeek(startOfMonth(referenceDate), { weekStartsOn: 1 });
  const gridEnd = endOfWeek(endOfMonth(referenceDate), { weekStartsOn: 1 });

  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });

  // Pad to GRID_SIZE cells for stable layout
  while (days.length < GRID_SIZE) {
    days.push(addDays(days[days.length - 1], 1));
  }

  return days.slice(0, GRID_SIZE).map((date) => ({
    date,
    dateKey: format(date, 'yyyy-MM-dd'),
    isCurrentMonth: isSameMonth(date, referenceDate),
    isToday: isToday(date),
  }));
}

/** Query range that covers the visible grid (including padding days) */
export function getMonthQueryRange(referenceDate: Date) {
  const gridStart = startOfWeek(startOfMonth(referenceDate), { weekStartsOn: 1 });
  const gridEnd = addDays(gridStart, GRID_SIZE - 1);
  return {
    fecha_desde: format(gridStart, 'yyyy-MM-dd'),
    fecha_hasta: format(gridEnd, 'yyyy-MM-dd'),
  };
}

// ─── Draggable chip ────────────────────────────────────────────────────────

interface ChipProps {
  actividad: ActividadListItem;
  onClick: (id: string) => void;
}

function DraggableChip({ actividad, onClick }: ChipProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: actividad.id,
    data: { actividad },
  });

  const isCompleted = actividad.estado === 'COMPLETADA';
  const isCancelled = actividad.estado === 'CANCELADA';
  const colors = PRIORITY_CHIP_COLORS[actividad.prioridad] || PRIORITY_CHIP_COLORS.B;

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={(e) => { e.stopPropagation(); onClick(actividad.id); }}
      title={actividad.titulo}
      className={cn(
        'text-[11px] leading-tight px-1.5 py-0.5 rounded truncate cursor-grab active:cursor-grabbing select-none',
        colors,
        (isCompleted || isCancelled) && 'line-through opacity-50',
        isDragging && 'opacity-30',
      )}
    >
      {actividad.titulo}
    </div>
  );
}

function OverlayChip({ actividad }: { actividad: ActividadListItem }) {
  const colors = PRIORITY_CHIP_COLORS[actividad.prioridad] || PRIORITY_CHIP_COLORS.B;
  return (
    <div className={cn(
      'text-[11px] leading-tight px-1.5 py-0.5 rounded truncate shadow-lg max-w-[140px]',
      colors,
    )}>
      {actividad.titulo}
    </div>
  );
}

// ─── Droppable day cell ─────────────────────────────────────────────────────

interface DayCellProps {
  day: CalendarDay;
  actividades: ActividadListItem[];
  isOver: boolean;
  onClickActividad: (id: string) => void;
  onClickDay: (date: Date) => void;
  onAddActividad: (dateKey: string) => void;
}

function DayCell({ day, actividades, isOver, onClickActividad, onClickDay, onAddActividad }: DayCellProps) {
  const { setNodeRef } = useDroppable({ id: day.dateKey });
  const visible = actividades.slice(0, MAX_VISIBLE);
  const overflow = actividades.length - MAX_VISIBLE;

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'min-h-[100px] border-t border-l p-1 transition-colors relative group/cell',
        !day.isCurrentMonth && 'bg-muted/30',
        day.isToday && 'bg-primary/5',
        isOver && 'bg-primary/10 ring-1 ring-primary/30',
      )}
    >
      {/* Day number + add button */}
      <div className="flex items-center justify-between mb-0.5">
        <button
          type="button"
          onClick={() => onClickDay(day.date)}
          className={cn(
            'text-xs font-medium h-5 w-5 rounded-full flex items-center justify-center transition-colors hover:bg-primary/10',
            day.isToday && 'bg-primary text-primary-foreground hover:bg-primary/90',
            !day.isCurrentMonth && 'text-muted-foreground/50',
          )}
        >
          {day.date.getDate()}
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onAddActividad(day.dateKey); }}
          className="h-4 w-4 rounded flex items-center justify-center text-muted-foreground/30 hover:text-primary hover:bg-primary/10 opacity-0 group-hover/cell:opacity-100 transition-opacity"
          title="Nueva actividad"
        >
          <Plus className="h-3 w-3" />
        </button>
      </div>

      {/* Activity chips */}
      <div className="space-y-0.5">
        {visible.map((act) => (
          <DraggableChip key={act.id} actividad={act} onClick={onClickActividad} />
        ))}
        {overflow > 0 && (
          <button
            type="button"
            onClick={() => onClickDay(day.date)}
            className="text-[10px] text-muted-foreground hover:text-primary px-1"
          >
            +{overflow} más
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────

interface CalendarioMesProps {
  currentDate: Date;
  onDateChange: (date: Date) => void;
  actividades: ActividadListItem[];
  isLoading?: boolean;
  onClickActividad: (id: string) => void;
  onViewDay: (date: Date) => void;
  onCreateActividad: (fecha: string) => void;
  onReschedule: (id: string, newDate: string) => void;
}

export function CalendarioMes({
  currentDate,
  onDateChange,
  actividades,
  isLoading,
  onClickActividad,
  onViewDay,
  onCreateActividad,
  onReschedule,
}: CalendarioMesProps) {
  const [draggingActivity, setDraggingActivity] = useState<ActividadListItem | null>(null);
  const [overDayKey, setOverDayKey] = useState<string | null>(null);

  const grid = useMemo(() => getMonthGrid(currentDate), [currentDate]);

  // Group activities by date key
  const actividadesPorDia = useMemo(() => {
    const map: Record<string, ActividadListItem[]> = {};
    for (const act of actividades) {
      const fecha = act.fecha_agendada;
      if (!fecha) continue;
      const key = fecha.split('T')[0];
      if (!map[key]) map[key] = [];
      map[key].push(act);
    }
    return map;
  }, [actividades]);

  // All activities mapped by ID for drag overlay
  const actividadesMap = useMemo(() => {
    const map = new Map<string, ActividadListItem>();
    for (const act of actividades) map.set(act.id, act);
    return map;
  }, [actividades]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const act = actividadesMap.get(String(event.active.id));
    if (act) setDraggingActivity(act);
  }, [actividadesMap]);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    setOverDayKey(event.over ? String(event.over.id) : null);
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setDraggingActivity(null);
    setOverDayKey(null);

    const { active, over } = event;
    if (!over) return;

    const actId = String(active.id);
    const newDateKey = String(over.id);
    const act = actividadesMap.get(actId);
    if (!act) return;

    // Only reschedule if date actually changed
    const currentDateKey = act.fecha_agendada?.split('T')[0];
    if (currentDateKey === newDateKey) return;

    onReschedule(actId, newDateKey);
  }, [actividadesMap, onReschedule]);

  const handlePrevMonth = () => onDateChange(subMonths(currentDate, 1));
  const handleNextMonth = () => onDateChange(addMonths(currentDate, 1));
  const handleToday = () => onDateChange(new Date());

  return (
    <div className="space-y-2">
      {/* Header: month navigation */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handlePrevMonth}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-base font-semibold min-w-[160px] text-center capitalize">
            {format(currentDate, 'MMMM yyyy', { locale: es })}
          </h2>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleNextMonth}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <Button variant="outline" size="sm" onClick={handleToday}>
          Hoy
        </Button>
      </div>

      {/* Calendar grid */}
      {isLoading ? (
        <div className="border rounded-lg bg-background flex items-center justify-center py-20">
          <div className="text-sm text-muted-foreground">Cargando actividades...</div>
        </div>
      ) : (
        <DndContext
          collisionDetection={pointerWithin}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <div className="border rounded-lg overflow-hidden bg-background">
            {/* Weekday header */}
            <div className="grid grid-cols-7 border-b">
              {WEEKDAYS.map((name) => (
                <div
                  key={name}
                  className="h-8 flex items-center justify-center text-xs font-medium text-muted-foreground bg-muted/50"
                >
                  {name}
                </div>
              ))}
            </div>

            {/* Day cells: 6 rows × 7 cols */}
            <div className="grid grid-cols-7 border-b">
              {grid.map((day) => (
                <DayCell
                  key={day.dateKey}
                  day={day}
                  actividades={actividadesPorDia[day.dateKey] ?? []}
                  isOver={overDayKey === day.dateKey}
                  onClickActividad={onClickActividad}
                  onClickDay={onViewDay}
                  onAddActividad={onCreateActividad}
                />
              ))}
            </div>
          </div>

          <DragOverlay>
            {draggingActivity ? <OverlayChip actividad={draggingActivity} /> : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Legend */}
      <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
        {PRIORITY_LABELS.map(([key, label]) => (
          <div key={key} className="flex items-center gap-1">
            <div className={cn('w-2.5 h-2.5 rounded', PRIORITY_CHIP_COLORS[key]?.split(' ')[0])} />
            <span>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
